import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { CheckCircle, CaretDown } from "@phosphor-icons/react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/layout/heading";
import { ActiveAlerts } from "@/components/brand-governance/overview/active-alerts";
import { Stats } from "@/components/brand-governance/overview/stats";
import { SubBrandHealth } from "@/components/brand-governance/overview/sub-brand-health";
import { Trending } from "@/components/brand-governance/overview/trending";

import { cn, formatSnakeCase, getDataRangePreset } from "@/lib/utils";
import { useSession } from "@/hooks/use-session";
import { BrandGovernanceApiFactory, type CreativesScorecardParams } from "@/apis/brand-os/brand-governance";
import { mDateRanges, type dateRangeType } from "@/mock/brand-governance";

function creativeScoreParams(brandName: string, rangeOption: dateRangeType, startDate: Date, endDate: Date | undefined, selectedChannel: string): CreativesScorecardParams {
  const start_date = format(startDate, "yyyy-MM-dd");
  const end_date = format(endDate ?? startDate, "yyyy-MM-dd");
  return {
    brand_name: brandName,
    channels: selectedChannel !== "all" ? [selectedChannel] : [],
    sub_brands: [],
    period: rangeOption,
    ...(rangeOption === "custom" ? { start_date, end_date } : {}),
  };
}

export const Route = createFileRoute("/dashboard/governance/_layout/overview")({
  component: GovernanceOverview,
});

export function GovernanceOverview() {
  const session = useSession();
  const brandName = session?.profile?.company?.company_name ?? "";

  const initialPreset = getDataRangePreset("MTD");
  const [rangeOption, setRangeOption] = useState<dateRangeType>("MTD");
  const [startDate, setStartDate] = useState<Date>(() => initialPreset.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(() => initialPreset.endDate);
  const [selectedChannel, setSelectedChannel] = useState<string>("all");

  const params = useMemo(() => creativeScoreParams(brandName, rangeOption, startDate, endDate, selectedChannel), [brandName, rangeOption, startDate, endDate, selectedChannel]);

  return (
    <div className="space-y-6">
      <PageHeader title="Brand Governance" />
      <Filters
        brandName={brandName}
        rangeOption={rangeOption}
        setRangeOption={setRangeOption}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedChannel={selectedChannel}
        setSelectedChannel={setSelectedChannel}
      />
      <Stats params={params} />
      <ActiveAlerts params={{ brand_name: brandName }} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_2fr]">
        <Trending params={params} />
        <SubBrandHealth params={params} />
      </div>
    </div>
  );
}

type FiltersProps = {
  brandName: string;
  rangeOption: dateRangeType;
  setRangeOption: React.Dispatch<React.SetStateAction<dateRangeType>>;
  startDate: Date;
  setStartDate: React.Dispatch<React.SetStateAction<Date>>;
  endDate: Date | undefined;
  setEndDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
  selectedChannel: string;
  setSelectedChannel: React.Dispatch<React.SetStateAction<string>>;
};

function Filters({ brandName, rangeOption, setRangeOption, startDate, setStartDate, endDate, setEndDate, selectedChannel, setSelectedChannel }: FiltersProps) {
  const { data: BrandOverview } = useQuery({
    ...BrandGovernanceApiFactory.Queries.BrandOverview(brandName),
    enabled: Boolean(brandName),
  });

  const channels = useMemo(() => {
    const channelData = BrandOverview?.data?.channels ?? [];
    return [...channelData.map((id) => ({ value: id, label: formatSnakeCase(id) })), { value: "all", label: "All channels" }];
  }, [BrandOverview?.data?.channels]);

  const [presetPopoverOpen, setPresetPopoverOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const onChangePresetPopoverOpen = (open: boolean) => {
    if (open) setCalendarOpen(false);
    setPresetPopoverOpen(open);
  };

  const onSelectPreset = (value: dateRangeType) => {
    setRangeOption(value);
    if (value === "custom") {
      setCalendarOpen(true);
      return;
    }
    const { startDate: nextStart, endDate: nextEnd } = getDataRangePreset(value);
    setStartDate(nextStart);
    setEndDate(nextEnd);
    onChangePresetPopoverOpen(false);
  };

  const onSelectDateRange = (range?: DateRange) => {
    if (!range?.from) return;
    if (!range.to || range.from.getTime() === range.to.getTime()) {
      setStartDate(range.from);
      setEndDate(range.from);
      return;
    }
    setStartDate(range.from);
    if (range.to) setEndDate(range.to);
  };

  return (
    <div className="flex items-end gap-3 p-2">
      <div className="flex flex-col gap-2">
        <Label>Select Date Range</Label>
        <Popover open={presetPopoverOpen} onOpenChange={onChangePresetPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={presetPopoverOpen} className="h-9 min-w-60 justify-between rounded-lg border-input bg-card px-3">
              <span className="truncate">
                {rangeOption === "custom"
                  ? endDate
                    ? `${format(startDate, "dd MMM yyyy")} - ${format(endDate, "dd MMM yyyy")}`
                    : "Custom"
                  : mDateRanges.find((o) => o.value === rangeOption)?.label ?? rangeOption}
              </span>
              <CaretDown className="size-3.5 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-60 rounded-lg p-0" align="start">
            {!calendarOpen ? (
              <Command>
                <CommandList>
                  <CommandGroup>
                    {mDateRanges.map((opt) => (
                      <CommandItem key={opt.value} value={opt.value} onSelect={() => onSelectPreset(opt.value)} className="justify-between">
                        {opt.label}
                        <CheckCircle weight="fill" className={cn("size-4.5 text-foreground", rangeOption === opt.value ? "opacity-100" : "opacity-0")} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            ) : (
              <Calendar
                mode="range"
                selected={{ from: startDate, to: endDate }}
                classNames={{ head_row: "flex mb-1", row: "flex w-full mt-1.5" }}
                onSelect={onSelectDateRange}
                onDayClick={(day, modifiers) => {
                  if (modifiers.selected && day.getTime() === startDate.getTime()) {
                    setStartDate(day);
                    setEndDate(day);
                  }
                }}
                defaultMonth={startDate}
                initialFocus
              />
            )}
          </PopoverContent>
        </Popover>
      </div>

      <div className="h-10 border-r" />

      <div className="flex flex-col gap-2">
        <Label>Select Channel</Label>
        <Select value={selectedChannel} onValueChange={setSelectedChannel}>
          <SelectTrigger className="h-9 min-w-44 rounded-lg border-input bg-card" size="default">
            <SelectValue placeholder="Select channel" />
          </SelectTrigger>
          <SelectContent>
            {channels.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
